import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  FileText,
  CalendarCheck,
  FileCheck,
  BarChart3,
  Users,
  Building2,
  Crown,
  Plus,
  Moon,
  Rows3,
  LogOut,
  ClipboardCheck,
} from 'lucide-react';

import { useTheme } from '../../context/ThemeContext';
import { useDensity } from '../../context/DensityContext';
import { useAuth } from '../../context/AuthContext';

import './command-palette.css';

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  }
  return ctx;
}

interface PaletteItem {
  id: string;
  label: string;
  group: 'navigate' | 'actions' | 'admin';
  icon?: React.ComponentType<{ className?: string }>;
  shortcut?: string;
  keywords?: string[];
  onSelect: () => void;
  /** When false, the item is hidden (e.g. feature disabled for this user). */
  visible?: boolean;
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  const value = useMemo(() => ({ open, setOpen, toggle }), [open, toggle]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPaletteDialog />
    </CommandPaletteContext.Provider>
  );
}

function CommandPaletteDialog() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toggle: toggleTheme } = useTheme();
  const { toggle: toggleDensity } = useDensity();
  const { isSuperadmin, canManageTeam, isExternalAuditor, isGuest, logout } = useAuth();
  const canProfile = !isExternalAuditor && !isGuest;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const run = useCallback(
    (fn: () => void) => {
      setOpen(false);
      setTimeout(fn, 0);
    },
    [setOpen]
  );

  const items = useMemo<PaletteItem[]>(() => {
    const canAccessInternal = !isExternalAuditor && !isGuest;
    const defs: PaletteItem[] = [
      {
        id: 'nav:dashboard',
        label: t('commandPalette.commands.goDashboard'),
        group: 'navigate',
        icon: LayoutDashboard,
        shortcut: 'g d',
        onSelect: () => run(() => navigate('/dashboard')),
        visible: true,
      },
      {
        id: 'nav:policies',
        label: t('commandPalette.commands.goPolicies'),
        group: 'navigate',
        icon: FileText,
        shortcut: 'g p',
        onSelect: () => run(() => navigate('/policies')),
        visible: true,
      },
      {
        id: 'nav:activities',
        label: t('commandPalette.commands.goActivities'),
        group: 'navigate',
        icon: CalendarCheck,
        shortcut: 'g a',
        onSelect: () => run(() => navigate('/activities')),
        visible: canAccessInternal,
      },
      {
        id: 'nav:audit',
        label: t('commandPalette.commands.goAudit'),
        group: 'navigate',
        icon: FileCheck,
        onSelect: () => run(() => navigate('/audit')),
        visible: true,
      },
      {
        id: 'nav:performance',
        label: t('commandPalette.commands.goPerformance'),
        group: 'navigate',
        icon: BarChart3,
        onSelect: () => run(() => navigate('/performance')),
        visible: canAccessInternal,
      },
      {
        id: 'nav:readiness',
        label: t('commandPalette.commands.goReadiness', 'Go to readiness walkthrough'),
        group: 'navigate',
        icon: ClipboardCheck,
        onSelect: () => run(() => navigate('/readiness')),
        visible: canAccessInternal,
      },
      {
        id: 'nav:companyProfile',
        label: t('commandPalette.commands.goCompanyProfile', 'Go to company profile'),
        group: 'navigate',
        icon: Building2,
        onSelect: () => run(() => navigate('/profile/company')),
        visible: canProfile,
      },
      {
        id: 'nav:team',
        label: t('commandPalette.commands.goTeam'),
        group: 'navigate',
        icon: Users,
        onSelect: () => run(() => navigate('/profile/team')),
        visible: canManageTeam && canAccessInternal,
      },
      {
        id: 'nav:admin',
        label: t('commandPalette.commands.goAdmin'),
        group: 'admin',
        icon: Crown,
        onSelect: () => run(() => navigate('/admin')),
        visible: isSuperadmin,
      },
      {
        id: 'action:new-policy',
        label: t('commandPalette.commands.newPolicy'),
        group: 'actions',
        icon: Plus,
        shortcut: 'n',
        onSelect: () => run(() => navigate('/policies/wizard')),
        visible: canAccessInternal,
      },
      {
        id: 'action:toggle-theme',
        label: t('commandPalette.commands.toggleTheme'),
        group: 'actions',
        icon: Moon,
        onSelect: () => run(toggleTheme),
        visible: true,
      },
      {
        id: 'action:toggle-density',
        label: t('commandPalette.commands.toggleDensity'),
        group: 'actions',
        icon: Rows3,
        onSelect: () => run(toggleDensity),
        visible: true,
      },
      {
        id: 'action:sign-out',
        label: t('commandPalette.commands.signOut'),
        group: 'actions',
        icon: LogOut,
        onSelect: () => run(logout),
        visible: true,
      },
    ];
    return defs.filter((item) => item.visible !== false);
  }, [
    t,
    navigate,
    run,
    toggleTheme,
    toggleDensity,
    logout,
    canManageTeam,
    canProfile,
    isSuperadmin,
    isExternalAuditor,
    isGuest,
  ]);

  const groups = useMemo(() => {
    const by: Record<PaletteItem['group'], PaletteItem[]> = {
      navigate: [],
      actions: [],
      admin: [],
    };
    for (const item of items) by[item.group].push(item);
    return by;
  }, [items]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm p-4 pt-[10vh] animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
      aria-hidden={false}
    >
      <Command
        label="Command Palette"
        className="isoc-cmdk w-full max-w-xl rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-modal overflow-hidden"
        loop
      >
        <div className="flex items-center gap-2 border-b border-neutral-200 dark:border-neutral-800 px-4">
          <span className="text-neutral-400 dark:text-neutral-500 text-sm">⌘K</span>
          <Command.Input
            autoFocus
            placeholder={t('commandPalette.placeholder') ?? ''}
            className="flex-1 bg-transparent py-3.5 text-sm text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none"
          />
          <kbd className="hidden sm:inline text-[11px] text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 dark:border-neutral-700 rounded px-1.5 py-0.5">
            esc
          </kbd>
        </div>
        <Command.List className="max-h-[60vh] overflow-y-auto py-1">
          <Command.Empty className="px-4 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            {t('commandPalette.empty')}
          </Command.Empty>

          {(['navigate', 'actions', 'admin'] as const).map((groupKey) => {
            const groupItems = groups[groupKey];
            if (groupItems.length === 0) return null;
            return (
              <Command.Group
                key={groupKey}
                heading={t(`commandPalette.groups.${groupKey}`)}
                className="cmdk-group"
              >
                {groupItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={item.id}
                      value={`${item.group} ${item.label} ${item.keywords?.join(' ') ?? ''}`}
                      onSelect={item.onSelect}
                      className="cmdk-item"
                    >
                      {Icon ? (
                        <Icon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                      ) : null}
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.shortcut ? (
                        <span className="flex gap-1">
                          {item.shortcut.split(' ').map((key, i) => (
                            <kbd
                              key={`${item.id}-kbd-${i}`}
                              className="text-[10px] bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 dark:border-neutral-700 rounded px-1 py-0.5 text-neutral-500 dark:text-neutral-400"
                            >
                              {key}
                            </kbd>
                          ))}
                        </span>
                      ) : null}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            );
          })}
        </Command.List>
      </Command>
    </div>
  );
}
